import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { axiosInstance } from '../helpers'
import { VendorPerformanceAnalytics } from './useVendorPerformance'

export interface VendorPerformanceReportOrganization {
    id: number
    name: string
}

export interface VendorPerformanceReport {
    id: number
    status: 'PENDING' | 'RUNNING' | 'COMPLETE' | 'FAILED'
    start_date: string | null
    end_date: string | null
    include_all_organizations: boolean
    organizations: VendorPerformanceReportOrganization[]
    generated_by: number | null
    generated_by_name: string | null
    created_date: string
    completed_date: string | null
    error: string
    progress_current: number
    progress_total: number
    row_count: number
    rows?: VendorPerformanceAnalytics[]
}

interface VendorPerformanceReportStatus {
    id: number
    status: VendorPerformanceReport['status']
    progress_current: number
    progress_total: number
    error: string
}

interface GenerateVendorPerformanceReportBody {
    start_date?: string
    end_date?: string
    organization_ids?: number[]
}

const REPORTS_KEY = 'vendor-performance-reports'

export const useVendorPerformanceReports = (enabled = true) => {
    const queryClient = useQueryClient()

    const listQuery = useQuery<VendorPerformanceReport[], Error>({
        queryKey: [REPORTS_KEY],
        queryFn: async () => {
            const response = await axiosInstance.get<VendorPerformanceReport[]>(
                'stats/vendor-performance-reports/',
            )
            return response.data
        },
        enabled,
        staleTime: 30 * 1000,
        refetchOnWindowFocus: false,
    })

    const generateMutation = useMutation({
        mutationFn: async (body: GenerateVendorPerformanceReportBody) => {
            const response = await axiosInstance.post<VendorPerformanceReport>(
                'stats/vendor-performance-reports/',
                body,
            )
            return response.data
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [REPORTS_KEY] })
        },
    })

    const deleteMutation = useMutation({
        mutationFn: async (reportId: number) => {
            await axiosInstance.delete(
                `stats/vendor-performance-reports/${reportId}/`,
            )
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [REPORTS_KEY] })
        },
    })

    return {
        reports: listQuery.data ?? [],
        isLoading: listQuery.isLoading,
        isError: listQuery.isError,
        refetch: listQuery.refetch,
        generateReport: generateMutation.mutateAsync,
        isGenerating: generateMutation.isLoading,
        generateError: generateMutation.error,
        deleteReport: deleteMutation.mutateAsync,
        isDeleting: deleteMutation.isLoading,
    }
}

export const useVendorPerformanceReport = (
    reportId: number | null,
    enabled = true,
) => {
    const { data, isLoading, isFetching, isError, refetch } = useQuery<
        VendorPerformanceReport,
        Error
    >({
        queryKey: [REPORTS_KEY, reportId],
        queryFn: async () => {
            const response = await axiosInstance.get<VendorPerformanceReport>(
                `stats/vendor-performance-reports/${reportId}/`,
            )
            return response.data
        },
        enabled: !!reportId && enabled,
        staleTime: 60 * 1000,
        refetchOnWindowFocus: false,
    })

    return {
        report: data ?? null,
        rows: data?.rows ?? [],
        isLoading: isLoading || isFetching,
        isError,
        refetch,
    }
}

export const useVendorPerformanceReportStatus = (
    reportId: number | null,
    enabled = true,
) => {
    const queryClient = useQueryClient()

    const { data } = useQuery<VendorPerformanceReportStatus, Error>({
        queryKey: [REPORTS_KEY, reportId, 'status'],
        queryFn: async () => {
            const response = await axiosInstance.get<VendorPerformanceReportStatus>(
                `stats/vendor-performance-reports/${reportId}/status/`,
            )
            const payload = response.data
            queryClient.setQueryData<VendorPerformanceReport[]>(
                [REPORTS_KEY],
                (current) => {
                    if (!current) {
                        return current
                    }
                    return current.map((report) =>
                        report.id === payload.id
                            ? {
                                  ...report,
                                  status: payload.status,
                                  progress_current: payload.progress_current,
                                  progress_total: payload.progress_total,
                                  error: payload.error,
                              }
                            : report,
                    )
                },
            )
            if (payload.status === 'COMPLETE' || payload.status === 'FAILED') {
                queryClient.invalidateQueries({
                    queryKey: [REPORTS_KEY],
                    exact: true,
                })
            }
            return payload
        },
        enabled: !!reportId && enabled,
        refetchInterval: (query) => {
            if (!query) {
                return false
            }
            const latestData = (query as any).state?.data as
                | VendorPerformanceReportStatus
                | undefined
            if (
                latestData?.status === 'COMPLETE' ||
                latestData?.status === 'FAILED'
            ) {
                return false
            }
            return 3000
        },
        refetchOnWindowFocus: false,
    })

    return {
        status: data?.status ?? null,
        progressCurrent: data?.progress_current ?? 0,
        progressTotal: data?.progress_total ?? 0,
        error: data?.error ?? '',
    }
}
